import { Component } from '@theme/component';

/**
 * Default number of decimal places for most currencies.
 * @type {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision.
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};

/**
 * Utility helpers for currency formatting.
 */
class Utils {
  /**
   * Formats a monetary value using Shopify `money` filter placeholders.
   * @param {number} moneyValue - Monetary value in major currency units.
   * @param {string} currencySymbol - Currency code or symbol.
   * @param {string} moneyFormat - Money format template.
   * @returns {string} Formatted monetary value.
   */
  static formatMoney(moneyValue, currencySymbol = '', moneyFormat = '{{amount}}') {
    const template = Theme.price.money_format || moneyFormat;
    const currency = Theme.price.currency || currencySymbol;

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats a number with configurable thousands and decimal separators.
   * @param {number} moneyValue - Monetary value in major currency units.
   * @param {string} thousandsSeparator - Thousands separator.
   * @param {string} decimalSeparator - Decimal separator.
   * @param {number} precision - Number of decimal places.
   * @returns {string} Formatted monetary value.
   */
  static formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = moneyValue.toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * @param {string} str
   * @returns {{isMatch: boolean, text: string | null | undefined}}
   */
  static isPropertiesFormat(str) {
    const regex = /^properties\[([^\]]+)\]$/;
    const match = str.match(regex);

    return {
      isMatch: !!match,
      text: match ? match[1] : null,
    };
  }
}

/**
 * Handles group-level quantity and subtotal interactions for order rows.
 */
class OrderGroup extends Component {
  /** @type {HTMLSpanElement | null} */
  #subTotalItems = this.querySelector('.order-subtotal__text--items .order-subtotal__text-value') || null;

  /** @type {HTMLSpanElement | null} */
  #subTotalB2BMoney = this.querySelector('.order-subtotal__text--total .order-subtotal__text-value--b2b') || null;

  /** @type {HTMLSpanElement | null} */
  #subTotalRRPMoney = this.querySelector('.order-subtotal__text--total .order-subtotal__text-value--rrp') || null;

  /** @type {HTMLSpanElement | null}  */
  #totalB2BMoneyValue = document.querySelector('.order-total__text--total .order-total__text-value--b2b') || null;

  /** @type {HTMLSpanElement | null}  */
  #totalRRPMoneyValue = document.querySelector('.order-total__text--total .order-total__text-value--rrp') || null;

  /** @type {HTMLButtonElement | null} */
  #addToCartButton = this.querySelector('.order-subtotal__button button') || null;

  /** @type {NodeListOf<HTMLFormElement>} */
  #forms = this.querySelectorAll('form.shopify-product-form');

  /** @type {(event: CustomEvent) => void} */
  #boundQuantityChange = this.handleQuantityChange.bind(this);

  /**
   * Initializes listeners for order group interactions.
   * @returns {void}
   */
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('order:quantity:change', /** @type {EventListener} */ (this.#boundQuantityChange));
  }

  /**
   * Cleans up listeners for order group interactions.
   * @returns {void}
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('order:quantity:change', /** @type {EventListener} */ (this.#boundQuantityChange));
  }

  /**
   * Whether the order group is expanded.
   * @returns {boolean}
   */
  get open() {
    return this.hasAttribute('open');
  }

  /**
   * Sets the order group expanded state.
   * @param {boolean} value - Expanded state.
   * @returns {void}
   */
  set open(value) {
    this.toggleAttribute('open', value);
  }

  /**
   * Toggles the group expanded state.
   * @param {Event} event - Toggle trigger event.
   * @returns {void}
   */
  handleToggleDropDawn(event) {
    this.open = !this.open;
    if (event.target instanceof HTMLButtonElement) {
      event.target.setAttribute('aria-expanded', String(this.open));
    }
  }

  /**
   * Sends selected items to Spark cart.
   * @param {Event} _event - Trigger event.
   * @returns {Promise<void>}
   */
  // @ts-ignore
  async handleAddItems(_event) {
    this.#forms = this.querySelectorAll('form.shopify-product-form');
    /**
     * Payload sent to Spark cart API.
     * @type {Array<{sku: string, adjustQuantity: number,customAttributes:{ key: string, value: string }[]}>}
     */
    const sparkPayload = [];
    if (this.#forms?.length) {
      this.#forms.forEach((form) => {
        const formData = new FormData(form);
        const sku = formData.get('sku');
        if (!sku || typeof sku !== 'string') return;
        const adjustQuantity = Number(formData.get('quantity'));
        const customAttributes = /** @type {{ key: string, value: string }[]} */ ([]);

        for (const [name, value] of formData.entries()) {
          const { isMatch, text } = Utils.isPropertiesFormat(name);
          if (isMatch && text) {
            customAttributes.push({
              key: text,
              value: String(value),
            });
          }
        }

        sparkPayload.push({
          sku,
          adjustQuantity,
          customAttributes: customAttributes,
        });
      });
    }
    if (sparkPayload.length === 0 && typeof window.spark === 'undefined') return;

    try {
      await window.spark.updateCart({ products: sparkPayload });
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * @typedef {Object} OrderQuantityChangeDetail
   * @property {number} localQuantity - Quantity for the updated row.
   * @property {number} groupQuantity - Total quantity in the group.
   * @property {HTMLInputElement[]} inputs - Quantity inputs in the group.
   * @property {HTMLElement | null} source - Source row element (`.product-th`).
   */

  /**
   * Handles quantity changes and updates totals.
   * @param {CustomEvent<OrderQuantityChangeDetail>} event - Quantity change event.
   * @returns {void}
   */
  handleQuantityChange(event) {
    const { localQuantity, groupQuantity, inputs, source } = event.detail;

    const formComponent = /** @type {HTMLElement | null} */ (source?.querySelector('product-form-component'));
    const totalPriceComponent = /** @type {OrderSparkPrice | null} */ (
      source?.querySelector('.product-th__total order-spark-price')
    );
    const totalPriceComponents = /** @type {NodeListOf<OrderSparkPrice>} */ (
      this?.querySelectorAll('.product-th__total order-spark-price')
    );

    if (typeof groupQuantity === 'number' && this.#subTotalItems) {
      this.#subTotalItems.textContent = String(groupQuantity);
      if (this.#addToCartButton) this.#addToCartButton.toggleAttribute('disabled', groupQuantity === 0);
    }
    if (formComponent) {
      formComponent?.toggleAttribute('disabled', localQuantity === 0);
    }
    if (totalPriceComponent) {
      const quantity = Math.max(localQuantity, 0);
      const b2bPriceContainer = /** @type {HTMLSpanElement | null} */ (
        totalPriceComponent.querySelector('.order-spark-price__b2b')
      );
      const rrpPriceContainer = /** @type {HTMLSpanElement | null} */ (
        totalPriceComponent.querySelector('.order-spark-price__rpr')
      );

      const { b2bPrice, rprPrice, updatePrice } = totalPriceComponent;
      updatePrice.bind(totalPriceComponent)(b2bPriceContainer, b2bPrice, quantity);
      updatePrice.bind(totalPriceComponent)(rrpPriceContainer, rprPrice, quantity);
      totalPriceComponent.quantityPrice = quantity;
    }

    let subTotalB2BPrice = 0;
    let subTotalRRPPrice = 0;

    totalPriceComponents.forEach((priceComponent) => {
      const { b2bPrice, rprPrice, quantityPrice } = priceComponent;
      subTotalB2BPrice += b2bPrice * quantityPrice;
      subTotalRRPPrice += rprPrice * quantityPrice;
    });

    if (this.#subTotalB2BMoney) {
      this.#subTotalB2BMoney.textContent = Utils.formatMoney(subTotalB2BPrice);
    }
    if (this.#subTotalRRPMoney) {
      this.#subTotalRRPMoney.textContent = Utils.formatMoney(subTotalRRPPrice);
    }
  }

  /**
   * Updates the global currency format and resets all total price elements to zero
   * using the new currency code. Called once when the first Spark price response
   * reveals a currency different from the current theme currency.
   *
   * @param {string} currencyCode - ISO currency code or symbol to apply (e.g. `'€'`, `'$'`).
   * @returns {void}
   */
  hadleUpdateInitalTotalPrices(currencyCode) {
    Theme.price.money_format = Theme.price.money_format.replace(Theme.price.currency, currencyCode);
    Theme.price.currency = currencyCode;

    [this.#subTotalB2BMoney, this.#subTotalRRPMoney, this.#totalB2BMoneyValue, this.#totalRRPMoneyValue].forEach(
      (moneyElement) => {
        if (moneyElement) {
          moneyElement.textContent = Utils.formatMoney(0);
        }
      },
    );
  }
}

if (!customElements.get('order-group')) {
  customElements.define('order-group', OrderGroup);
}

/**
 * @typedef {Object} Refs
 * @property {HTMLButtonElement[]} quantityButton - Quantity action buttons.
 * @property {HTMLInputElement[]} quantityInput - Quantity inputs.
 */

/**
 * Product order row controller.
 * @extends {Component<Refs>}
 */
class ProductOrder extends Component {
  /** @type {IntersectionObserver | null} Shared observer for all product-order elements. */
  static #sharedIntersectionObserver = null;

  requiredRefs = ['quantityButton', 'quantityInput'];

  /** @type {OrderGroup | null} */
  #orderGroup = this.closest('order-group');

  /** @type {Array<Record<string, any>>} */
  #customVariants = [];

  /** @type {NodeListOf<HTMLSelectElement>} */
  #selects = this.querySelectorAll('select[data-position]');

  /** @type {Record<"option1" | "option2" | "option3", string | null>} */
  #options = {
    option1: null,
    option2: null,
    option3: null,
  };

  /** @type {string | undefined} */
  #sectionId = this.dataset.sectionId;

  /** @type {string | undefined} */
  #productHref = this.dataset.productHref;

  /** @type {string} */
  #uniqOrder = this.dataset.uniqOrder || '';

  /** @type {boolean} */
  #isProductInformationLoaded = false;

  /** @type {Map<string, string>} Cached section HTML by variant ID. */
  #variantHtmlCache = new Map();

  /**
   * Initializes variant state and deferred product price loading.
   * @returns {void}
   */
  connectedCallback() {
    super.connectedCallback();
    this.#customVariants = this.getProductVariants('[data-custom-product-json]');

    this.#selects.forEach((select) => {
      const {
        value,
        dataset: { position },
      } = select;

      if (position && this.isValidOptionPosition(position)) {
        this.#options[position] = value;
      }
    });

    this.#setupIntersectionObserver();
  }

  /**
   * Disconnects observer used for deferred product price loading.
   * @returns {void}
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    ProductOrder.#sharedIntersectionObserver?.unobserve(this);
  }

  /**
   * Whether the product row is expanded.
   * @returns {boolean}
   */
  get open() {
    return this.hasAttribute('open');
  }

  /**
   * Sets the product row expanded state.
   * @param {boolean} value - Expanded state.
   * @returns {void}
   */
  set open(value) {
    this.toggleAttribute('open', value);
  }

  /**
   * Toggles row expanded state.
   * @param {Event} event - Toggle trigger event.
   * @returns {void}
   */
  handleToggleDropDawn(event) {
    this.open = !this.open;
    if (event.target instanceof HTMLButtonElement) {
      event.target.setAttribute('aria-expanded', String(this.open));
    }
  }

  /**
   * Updates quantity input using plus/minus controls.
   * @param {Event} event - Button click event.
   * @returns {void}
   */
  updateQuantity(event) {
    const { target } = event;
    if (target instanceof HTMLElement) {
      const thQuantity = target.closest('div.product-th__quantity');
      const input = thQuantity?.querySelector('input');
      const button = target.closest('button');
      const name = button?.name;

      if (input instanceof HTMLInputElement && typeof name == 'string' && ['plus', 'minus'].includes(name)) {
        const nameMethod = name == 'minus' ? 'stepDown' : 'stepUp';
        input[nameMethod]();
        input.dispatchEvent(new Event('change'));
      }
    }
  }

  /**
   * Emits group-level quantity change event.
   * @param {Event} event - Quantity change event.
   * @returns {void}
   */
  changeQuantity(event) {
    let currentOrderGroup = /** @type {OrderGroup | null} */ (null);
    let currentProductTh = /** @type {HTMLElement | null} */ (null);
    let localQuantity = 0;
    let hasMoreOneProductOrder = false;

    if (event.target instanceof HTMLInputElement) {
      currentOrderGroup = event.target.closest('order-group');
      currentProductTh = event.target.closest('.product-th');
      localQuantity = Number(event.target.value);
    }

    if (currentOrderGroup instanceof OrderGroup) {
      hasMoreOneProductOrder = currentOrderGroup.querySelectorAll('product-order').length > 1;
    }

    let { quantityInput } = this.refs;

    if (hasMoreOneProductOrder && currentOrderGroup instanceof OrderGroup) {
      const inputs = /** @type {HTMLInputElement[]} */ ([
        ...currentOrderGroup.querySelectorAll("input[ref='quantityInput[]']"),
      ]);
      if (inputs.length != 0) quantityInput = inputs;
    }

    const groupQuantity = quantityInput.reduce(
      (accumulator, input) => accumulator + (input.value ? Number(input.value) : 1),
      0,
    );

    if (this.#orderGroup instanceof OrderGroup) {
      const event = new CustomEvent('order:quantity:change', {
        bubbles: true,
        detail: {
          localQuantity: localQuantity,
          groupQuantity: groupQuantity,
          inputs: quantityInput,
          source: currentProductTh,
        },
      });

      this.#orderGroup.dispatchEvent(event);
    }
  }

  /**
   * Handles option selection changes and updates the selected variant.
   * @param {Event} event - Select event.
   * @returns {void}
   */
  onChange(event) {
    if (event.target instanceof HTMLSelectElement) {
      const select = event.target;
      const position = select.dataset.position;
      const value = select.value;
      if (position && this.isValidOptionPosition(position)) {
        this.changeVariant(position, value);
      }
    }
  }

  /**
   * Updates selected option values and swaps row blocks for the matched variant.
   * Uses cached section HTML per variant ID to avoid duplicate network requests.
   * @param {"option1" | "option2" | "option3"} optionName - Option position.
   * @param {string} value - Option value.
   * @returns {Promise<void>}
   */
  async changeVariant(optionName, value) {
    const variants = this.#customVariants;
    this.#options[optionName] = value;

    const selectedVariant = variants.find(
      ({ option1, option2, option3 }) =>
        option1 === this.#options.option1 && option2 === this.#options.option2 && option3 === this.#options.option3,
    );

    if (selectedVariant && this.#sectionId && this.#productHref) {
      const url = new URL(this.#productHref, window.location.origin);
      url.searchParams.set('section_id', this.#sectionId);
      url.searchParams.set('variant', selectedVariant.id);
      url.searchParams.set('view', 'api-ajax-dev');
      const variantId = String(selectedVariant.id);

      try {
        let responseText = this.#variantHtmlCache.get(variantId);
        if (!responseText) {
          const response = await fetch(url);
          responseText = await response.text();
          this.#variantHtmlCache.set(variantId, responseText);
        }
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const blockTypes = ['image', 'form', 'stock'];
        blockTypes.forEach((blockType) => {
          const currentBlock = this.querySelector(`[data-type="${blockType}"]`);

          const newBlock = html.querySelector(
            `product-order[data-uniq-order="${this.#uniqOrder}"] [data-type="${blockType}"]`,
          );

          if (blockType === 'form') {
            const formComponent = currentBlock?.querySelector('product-form-component');
            const newFormComponent = newBlock?.querySelector('product-form-component');
            if (formComponent && newFormComponent) {
              const form = formComponent.querySelector('form');
              const newForm = newFormComponent.querySelector('form');
              newForm?.setAttribute('id', form?.getAttribute('id') || '');
              const isDisabled = currentBlock?.querySelector('product-form-component')?.hasAttribute('disabled');
              if (!isDisabled) {
                newFormComponent.removeAttribute('disabled');
              }
            }
          }

          if (currentBlock && newBlock) {
            currentBlock.replaceWith(newBlock);
          }
        });
      } catch (error) {
        console.error(error);
      }
    }
  }

  /**
   * Parses variants JSON from a script node by selector.
   * @param {string} selector - HTML selector for JSON script tag.
   * @returns {Array<Record<string, any>>} Shopify variants array.
   */
  getProductVariants(selector) {
    try {
      const jsonText = this.querySelector(selector)?.textContent;
      if (jsonText) {
        return JSON.parse(jsonText);
      }
      return [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  /**
   * Type guard for supported option positions.
   * @param {string} optionPosition - Option position key.
   * @returns {optionPosition is "option1" | "option2" | "option3"}
   */
  isValidOptionPosition = (optionPosition) => {
    return ['option1', 'option2', 'option3'].includes(optionPosition);
  };

  /**
   * Initializes lazy product information loading when the row is visible.
   * @returns {void}
   */
  #setupIntersectionObserver() {
    if (!ProductOrder.#sharedIntersectionObserver) {
      ProductOrder.#sharedIntersectionObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || !(entry.target instanceof ProductOrder)) return;
            observer.unobserve(entry.target);
            entry.target.#loadProductInformation();
          });
        },
        { rootMargin: '50px' },
      );
    }

    ProductOrder.#sharedIntersectionObserver.observe(this);
  }

  /**
   * Loads product prices from Spark and updates row price components.
   * @returns {void}
   */
  #loadProductInformation() {
    if (this.#isProductInformationLoaded) return;
    this.#isProductInformationLoaded = true;

    this.getProductInformation(this.dataset.id || '').then((response) => {
      response.forEach((responseItem) => {
        const { price, rrp, sku } = responseItem;
        const priceComponent = this.querySelector(`.product-th__price order-spark-price[data-sku="${sku}"]`);
        const priceTotalComponent = this.querySelector(`.product-th__total order-spark-price[data-sku="${sku}"]`);
        if (!priceComponent || !priceTotalComponent) {
          return;
        }
        let { rate } = Shopify.currency;
        rate = '1';
        const currencyCode = price?.currencyCode == 'eur' ? '€' : '$';
        if (currencyCode !== Theme.price.currency) {
          this.#orderGroup?.hadleUpdateInitalTotalPrices(currencyCode);
        }
        const fallbackPrice = Number(priceComponent.getAttribute('data-fallback-price'));
        const b2bPrice = (price?.gross ? price.gross : fallbackPrice) * Number(rate || 1);
        const rprPrice = (rrp || fallbackPrice) * Number(rate || 1);

        if (priceComponent && 'b2bPrice' in priceComponent && 'rprPrice' in priceComponent) {
          priceComponent.b2bPrice = b2bPrice;
          priceComponent.rprPrice = rprPrice;
        }

        if (priceTotalComponent && 'b2bPrice' in priceTotalComponent && 'rprPrice' in priceTotalComponent) {
          priceTotalComponent.b2bPrice = b2bPrice;
          priceTotalComponent.rprPrice = rprPrice;
        }
      });
    });
  }

  /**
   * @typedef {Object} ProductInformation
   * @property {string} id - Product identifier.
   * @property {string} externalId - External product identifier.
   * @property {Variant[]} variants - Product variants.
   */

  /**
   * @typedef {Object} Variant
   * @property {string} id - Variant identifier.
   * @property {string} externalId - External variant identifier.
   * @property {string} parentExternalId - External parent product identifier.
   * @property {"in_stock" | "backorder" | "out_of_stock"} stockStatus - Stock status.
   * @property {string | null} restockDate - Restock date, if present.
   * @property {number} stockQty - Available stock quantity.
   * @property {string} cartImageUrl - Cart image URL.
   * @property {number} position - Sort position.
   * @property {string} sku - Variant SKU.
   * @property {string} barcode - Barcode value.
   * @property {string} slug - URL slug.
   * @property {string} name - Variant name.
   * @property {Price} price - Variant price.
   * @property {number} rrp - Recommended retail price.
   * @property {RrpV2} rrpV2 - Alternate RRP representation.
   * @property {PriceBreak[]} priceBreaks - Price break list.
   * @property {any[]} settings - Additional settings.
   * @property {Option[]} options - Variant options.
   */

  /**
   * @typedef {Object} Price
   * @property {number} taxRate - Tax rate value.
   * @property {number} net - Net amount.
   * @property {number} gross - Gross amount.
   * @property {string} currencyCode - ISO currency code.
   */

  /**
   * @typedef {Object} RrpV2
   * @property {string} number - RRP numeric value.
   * @property {string} currency - RRP currency code.
   */

  /**
   * @typedef {Object} PriceBreak
   * @property {number} quantity - Minimum quantity for the tier.
   * @property {Price} price - Tier price.
   * @property {string | null} unitOfMeasure - Unit of measure.
   */

  /**
   * @typedef {Object} Option
   * @property {string} group - Option group name.
   * @property {string} value - Option value.
   */

  /**
   * Fetches product information from Spark API.
   * @param {string} productId - Spark product identifier.
   * @returns {Promise<ProductInformation["variants"]>} Product variants.
   */

  async getProductInformation(productId) {
    try {
      const response = /** @type { ProductInformation } */ (await window.spark.getProduct(productId));
      const { variants } = response;
      return variants;
    } catch (error) {
      console.error(error);
      return [];
    }
  }
}

if (!customElements.get('product-order')) {
  customElements.define('product-order', ProductOrder);
}

/**
 * Renders and updates B2B/RRP price values for an order row.
 */
class OrderSparkPrice extends Component {
  /** @type {HTMLSpanElement | null} */
  #b2bPriceContainer = this.querySelector('.order-spark-price__b2b');

  /** @type {HTMLSpanElement | null} */
  #rprPriceContainer = this.querySelector('.order-spark-price__rpr');

  /** @type {number} */
  #b2bPrice = 0;

  /** @type {number} */
  #rprPrice = 0;

  /** @type {number} */
  #quantityPrice = 0;

  /** @type {boolean} */
  #isUnitPrice = this.hasAttribute('data-unit-text');

  /**
   * Lifecycle hook for custom element mount.
   * @returns {void}
   */
  connectedCallback() {
    super.connectedCallback();
  }

  /**
   * Lifecycle hook for custom element unmount.
   * @returns {void}
   */
  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Current quantity multiplier.
   * @returns {number}
   */
  get quantityPrice() {
    return this.#quantityPrice;
  }

  /**
   * Sets current quantity multiplier.
   * @param {number} value - Quantity multiplier.
   * @returns {void}
   */
  set quantityPrice(value) {
    this.#quantityPrice = value;
  }

  /**
   * Current B2B unit price.
   * @returns {number}
   */
  get b2bPrice() {
    return this.#b2bPrice;
  }

  /**
   * Sets B2B unit price and re-renders the amount.
   * @param {number} value - B2B unit price.
   * @returns {void}
   */
  set b2bPrice(value) {
    this.#b2bPrice = value;
    this.updatePrice(this.#b2bPriceContainer, value, 1);
  }

  /**
   * Current RRP unit price.
   * @returns {number}
   */
  get rprPrice() {
    return this.#rprPrice;
  }

  /**
   * Sets RRP unit price and re-renders the amount.
   * @param {number} value - RRP unit price.
   * @returns {void}
   */
  set rprPrice(value) {
    this.#rprPrice = value;
    this.updatePrice(this.#rprPriceContainer, value, 1);
  }

  /**
   * Renders a price for the specified quantity.
   * @param {HTMLSpanElement | null} priceContainer - Target price container.
   * @param {number} price - Unit price.
   * @param {number} quantity - Quantity multiplier.
   * @returns {void}
   */
  updatePrice(priceContainer, price, quantity = 1) {
    if (priceContainer instanceof HTMLElement) {
      const maxQuantity = Math.max(quantity, 1);
      priceContainer.textContent = Utils.formatMoney(price * maxQuantity);
      if (this.#isUnitPrice) {
        const unitText = this.getAttribute('data-unit-text');
        priceContainer.textContent = `${priceContainer.textContent} ${unitText}`;
      }
      this.toggleLoadPrice(true);
    }
  }

  /**
   * Toggles `data-loaded` attribute.
   * @param {boolean} isLoaded - Whether the price is loaded.
   * @returns {void}
   */
  toggleLoadPrice(isLoaded) {
    this.setAttribute('data-loaded', String(isLoaded));
  }
}

if (!customElements.get('order-spark-price')) {
  customElements.define('order-spark-price', OrderSparkPrice);
}

/**
 * Wrapper component for order-list total area.
 */
class OrderTotal extends Component {
  /** @type {HTMLElement | null} Root order list element. */
  #orderList = this.closest('.order-list');

  /** @type {NodeListOf<OrderSparkPrice>} Total price components within the related order list. */
  #totalSparkPrices =
    this.#orderList?.querySelectorAll('.product-th__total order-spark-price') ||
    this.querySelectorAll('.product-th__total order-spark-price');

  /** @type {HTMLSpanElement | null} Total items value node. */
  #totalItemsValue = this.querySelector('.order-total__text--items .order-total__text-value');

  /** @type {HTMLSpanElement | null} Total B2B amount value node. */
  #totalB2BMoneyValue = this.querySelector('.order-total__text--total .order-total__text-value--b2b');

  /** @type {HTMLSpanElement | null} Total RRP amount value node. */
  #totalRRPMoneyValue = this.querySelector('.order-total__text--total .order-total__text-value--rrp');

  /** @type {HTMLButtonElement | null} Add-to-cart button in total block. */
  #addToCartButton = this.querySelector('.order-total__button button');

  /** @type {(event: Event) => void} */
  #boundQuantityChange = this.handleQuantityChange.bind(this);

  /** @type {(event: Event) => void} */
  #boundAddItems = this.handleAddItems.bind(this);

  /**
   * Lifecycle hook for custom element mount.
   * @returns {void}
   */
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('order:quantity:change', this.#boundQuantityChange);
    this.#addToCartButton?.addEventListener('click', this.#boundAddItems);
    this.#updateTotals();
  }

  /**
   * Lifecycle hook for custom element unmount.
   * @returns {void}
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('order:quantity:change', this.#boundQuantityChange);
    this.#addToCartButton?.removeEventListener('click', this.#boundAddItems);
  }

  /**
   * Handles quantity changes from the related order list.
   * @param {Event} event - Source quantity change event.
   * @returns {void}
   */
  handleQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.#orderList?.contains(event.target)) return;
    this.#updateTotals();
  }

  /**
   * Sends selected items from the whole order list to Spark cart.
   * @param {Event} _event - Click event.
   * @returns {Promise<void>}
   */
  async handleAddItems(_event) {
    /**
     * Payload sent to Spark cart API.
     * @type {Array<{sku: string, adjustQuantity: number,customAttributes:{ key: string, value: string }[]}>}
     */
    const sparkPayload = [];

    /**
     * @type {NodeListOf<HTMLFormElement> | []}
     */
    const forms = this.#orderList?.querySelectorAll('form.shopify-product-form') || [];
    forms.forEach((form) => {
      const formData = new FormData(form);
      const sku = formData.get('sku');
      if (!sku || typeof sku !== 'string') return;

      const adjustQuantity = Number(formData.get('quantity'));
      const customAttributes = /** @type {{ key: string, value: string }[]} */ ([]);

      for (const [name, value] of formData.entries()) {
        const { isMatch, text } = Utils.isPropertiesFormat(name);
        if (isMatch && text) {
          customAttributes.push({
            key: text,
            value: String(value),
          });
        }
      }
      sparkPayload.push({
        sku,
        adjustQuantity,
        customAttributes: customAttributes,
      });
    });

    if (sparkPayload.length === 0 && typeof window.spark === 'undefined') return;

    try {
      await window.spark.updateCart({ products: sparkPayload });
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Calculates and updates total quantity and total price values.
   * @returns {void}
   */
  #updateTotals() {
    this.#totalSparkPrices =
      this.#orderList?.querySelectorAll('.product-th__total order-spark-price') || this.#totalSparkPrices;

    let totalQuantity = 0;
    let totalB2BPrice = 0;
    let totalRRPPrice = 0;

    this.#totalSparkPrices.forEach((priceComponent) => {
      const row = priceComponent.closest('.product-th');
      const quantityInput = row?.querySelector('.product-th__quantity input');
      const quantityFromInput =
        quantityInput instanceof HTMLInputElement ? Number(quantityInput.value || 0) : Number.NaN;
      const quantity =
        Number.isFinite(quantityFromInput) && quantityFromInput >= 0
          ? quantityFromInput
          : Math.max(priceComponent.quantityPrice || 0, 0);

      totalQuantity += quantity;
      totalB2BPrice += priceComponent.b2bPrice * quantity;
      totalRRPPrice += priceComponent.rprPrice * quantity;
    });

    if (this.#totalItemsValue) {
      this.#totalItemsValue.textContent = String(totalQuantity);
    }
    if (this.#totalB2BMoneyValue) {
      this.#totalB2BMoneyValue.textContent = Utils.formatMoney(totalB2BPrice);
    }
    if (this.#totalRRPMoneyValue) {
      this.#totalRRPMoneyValue.textContent = Utils.formatMoney(totalRRPPrice);
    }
    if (this.#addToCartButton) {
      this.#addToCartButton.toggleAttribute('disabled', totalQuantity === 0);
    }
  }

  /**
   * Opens Spark cart drawer from total block button.
   * @param {Event} event - Click event.
   * @returns {void}
   */
  openSparkDrawer(event) {
    if (event.target instanceof HTMLButtonElement) {
      const spark = Theme.openSparkDrawer;
      if (typeof spark != undefined) {
        spark('cart');
      }
    }
  }
}

if (!customElements.get('order-total')) {
  customElements.define('order-total', OrderTotal);
}

/**
 * @typedef {Object} InfiniteScrollConfig
 * @property {{ url?: string } | null | undefined} [next]
 */

class InfiniteScroll extends HTMLElement {
  static PRELOAD_OFFSET_PX = 300;

  /** @type {HTMLElement | null} */
  #parentContainer = /** @type {HTMLElement | null} */ (document.querySelector('[data-collection-body]'));

  /** @type {HTMLElement | null} */
  #trigger = /** @type {HTMLElement | null} */ (this.querySelector('.infinite-scroll__trigger'));

  /** @type {HTMLElement | null} */
  #loader = /** @type {HTMLElement | null} */ (this.querySelector('.infinite-scroll__loader'));

  /** @type {string | null} */
  #sectionId = this.getAttribute('data-section-id');

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {boolean} */
  #isLoading = false;

  constructor() {
    super();
  }

  /**
   * Initializes the observer when the element enters the DOM.
   * @returns {void}
   */
  connectedCallback() {
    if (!this.#trigger || !this.#parentContainer) {
      console.warn('InfiniteScroll: missing trigger or parent container.');
      return;
    }

    this.initObserver();
  }

  /**
   * Cleans up the observer when the element leaves the DOM.
   * @returns {void}
   */
  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  /**
   * Reads pagination config from the embedded JSON script.
   * @returns {InfiniteScrollConfig}
   */
  getConfig() {
    const script = this.querySelector('script[data-config]');
    if (!script) return {};

    try {
      return /** @type {InfiniteScrollConfig} */ (JSON.parse(script.textContent || '{}'));
    } catch (error) {
      console.error('Invalid JSON in data-config:', error instanceof Error ? error.message : error);
      return {};
    }
  }

  /**
   * Fetches the next page HTML.
   * @returns {Promise<Document | null>}
   */
  async fetchData() {
    const config = this.getConfig();

    if (!config.next?.url || !this.#sectionId) {
      this.cleanup();
      return null;
    }

    try {
      this.showLoader();

      const response = await fetch(`${config.next.url}&section_id=${this.#sectionId}`);
      const data = await response.text();

      return new DOMParser().parseFromString(data, 'text/html');
    } catch (error) {
      console.error('Fetch error:', error instanceof Error ? error.message : error);
      return null;
    } finally {
      this.hideLoader();
    }
  }

  /**
   * Appends fetched items and swaps the pagination config script.
   * @param {Document | null} html
   * @returns {void}
   */
  insertAndUpdateContent(html) {
    if (!html) return;

    const productList = html.querySelector('[data-collection-body]');
    const productCards = [...(productList?.children || [])];

    if (!productCards.length) return;

    const newConfig = html.querySelector('script[data-config]');
    const currentConfig = this.querySelector('script[data-config]');
    if (newConfig && currentConfig) {
      currentConfig.replaceWith(newConfig);
    }

    this.#parentContainer?.append(...productCards);

    this.#observer?.disconnect();
  }

  /**
   * Checks whether the trigger is already close enough to the viewport to load
   * the next page, even if the observer callback was missed during a fast scroll.
   * @returns {boolean}
   */
  isTriggerInLoadZone() {
    if (!this.#trigger || !this.isConnected) return false;

    const triggerBounds = this.#trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    return triggerBounds.top <= viewportHeight + InfiniteScroll.PRELOAD_OFFSET_PX;
  }

  /**
   * Loads the next page once and re-arms observation if more pages remain.
   * @returns {Promise<void>}
   */
  async loadNextPage() {
    if (this.#isLoading) return;

    this.#isLoading = true;

    try {
      const html = await this.fetchData();
      this.insertAndUpdateContent(html);
    } finally {
      this.#isLoading = false;

      if (!this.isConnected) return;

      this.initObserver();

      if (this.isTriggerInLoadZone()) {
        void this.loadNextPage();
      }
    }
  }

  /**
   * Observes the trigger and loads more items when it enters the viewport.
   * @returns {void}
   */
  initObserver() {
    if (!this.#trigger) return;

    this.#observer?.disconnect();

    this.#observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (entry?.isIntersecting && !this.#isLoading) {
          void this.loadNextPage();
        }
      },
      {
        rootMargin: `0px 0px ${InfiniteScroll.PRELOAD_OFFSET_PX}px 0px`,
        threshold: 0,
      },
    );

    this.#observer.observe(this.#trigger);

    if (this.isTriggerInLoadZone()) {
      void this.loadNextPage();
    }
  }

  /**
   * Shows the loader.
   * @returns {void}
   */
  showLoader() {
    if (this.#loader) this.#loader.hidden = false;
  }

  /**
   * Hides the loader.
   * @returns {void}
   */
  hideLoader() {
    if (this.#loader) this.#loader.hidden = true;
  }

  /**
   * Stops observing and removes the element.
   * @returns {void}
   */
  cleanup() {
    this.#observer?.disconnect();
    this.remove();
  }
}

window.customElements.define('infinite-scroll', InfiniteScroll);
